import { SchemaInterface } from '../../shared/types/schema-interface';
import { IdentifierType } from './member-interface';
import { SchemaConsents } from './schema/schema-consents';
import { SchemaProperties } from './schema/schema-properties';

const IDENTIFIER_TYPE = IdentifierType;

export class Schema {
  public consents = new SchemaConsents(this.schema);
  public properties = new SchemaProperties(this.schema);

  constructor(private schema: SchemaInterface) {}

  public getAvailableFieldNames(product = 'webforms'): string[] {
    const schemaProperties = Object.keys(this.schema.properties);
    const schemaPropertiesSet = new Set(schemaProperties);
    const productFieldNames = this.getAvailableProductFieldNames(product);

    if (productFieldNames) {
      return Array.from(new Set(productFieldNames.filter((x) => schemaPropertiesSet.has(x))));
    }

    return Array.from(schemaProperties);
  }

  public isFieldRequired(fieldName: string): boolean {
    return this.schema.required.includes(fieldName);
  }

  public getGroupedHash(fieldName: string): any {
    return this.getField(fieldName).groupedHash;
  }

  public getEnumValues(fieldName: string): string[] {
    return this.getField(fieldName).enum || this.getField(fieldName).items.enum;
  }

  public isGroupedHash(fieldName: string): boolean {
    return !!this.getGroupedHash(fieldName);
  }

  public isSingleSelectable(fieldName: string): boolean {
    return !!this.getField(fieldName).enum;
  }

  public isMultiSelectable(fieldName: string): boolean {
    return !!this.getField(fieldName).items && !this.getField(fieldName).groupedHash;
  }

  public isMultiCustom(fieldName: string): boolean {
    const field = this.getField(fieldName);
    return !!field.items && !field.items.enum;
  }

  public isDateField(fieldName: string): boolean {
    return this.getField(fieldName).format === 'date';
  }

  public isMsisdnField(fieldName: string): boolean {
    return this.getField(fieldName).format === 'msisdn';
  }

  public isEmailField(fieldName: string): boolean {
    return this.getField(fieldName).format === 'email';
  }

  public isBooleanField(fieldName: string): boolean {
    return this.getField(fieldName).type === 'boolean';
  }

  public isIdentifier(fieldName: string): boolean {
    return !!this.schema.identifiers?.includes(fieldName);
  }

  public isMsisdnIdentifier(): boolean {
    return !this.schema.identifiers || this.schema.identifiers.includes(IDENTIFIER_TYPE.MSISDN);
  }

  public isEmailIdentifier(): boolean {
    return !this.isMsisdnIdentifier() && !!this.schema.identifiers?.includes(IDENTIFIER_TYPE.EMAIL);
  }

  public hasMultipleIdentifiers(): boolean {
    return (this.schema?.identifiers?.length || 0) > 1;
  }

  public getNumberSteps(fieldName: string): number | null {
    if (this.getField(fieldName).type === 'integer') { return 1; }
    if (this.getField(fieldName).type === 'number') { return 0.000001; }
    return null;
  }

  public hasNumericValues(fieldName: string): boolean {
    if (this.getField(fieldName).type === 'integer' || this.getField(fieldName).type === 'number') {
      return true;
    }

    return !!this.getField(fieldName).items &&
      (this.getField(fieldName).items.type === 'integer' || this.getField(fieldName).items.type === 'number');
  }

  public getField(fieldName: string): any {
    return this.schema.properties[fieldName];
  }

  public isRequiredOrIdentifier(fieldName: string): boolean {
    return this.isFieldRequired(fieldName) || this.isIdentifier(fieldName) && !this.hasMultipleIdentifiers();
  }

  public isVisible(fieldName: string): boolean {
    if (this.isIdentifier(fieldName) || this.isFieldRequired(fieldName)) { return true; }
    return this.getField(fieldName).visibility !== false;
  }

  private getAvailableProductFieldNames(product: string): string[] | null {
    if (this.schema.products) {
      if (this.schema.products[product]) { return this.schema.products[product]; }
      if (this.schema.products.default) { return this.schema.products.default; }
    }

    return null;
  }
}
